import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { contractEmpenhosService } from '../../services/api';
import CascaDeModal from '../shared/CascaDeModal';
import { useConfirm } from '../shared/ConfirmDialog';
import { useAviso } from '../shared/Aviso';
import { useEstadoDaCarga, EstadoDaLista, mensagemDoErroDeCarga, campo, acaoPrincipal, acaoSecundaria } from '../../design';
import { TONE_BADGE, TONE_TEXT } from '../../utils/statusColors';
import { fmtCompetencia, fmtCurrency, fmtDate } from '../../utils/formatters';
import {
  FormularioDoEmpenho, NotaDeEmpenho, PreviaDoRetroConsumo, ROTULO_DO_AVISO, corpoDoEmpenho, ehPdf, escopoDoEmpenho, faixaDoEmpenho,
  fmtSaldo, formularioDe, formularioVazio, fraseDasCompetencias, fraseDoSaldo, oQueFalta, produtosDaAssinatura,
} from '../../utils/empenho';
import {
  CampoDoFormularioLido, LeituraDoEmpenho, ROTULO_DO_AVISO_DA_LEITURA, ROTULO_DO_RECONHECEDOR, ehLeitura, formularioDaLeitura,
  fraseDaFalhaDaLeitura, seloDoCampo, tomDoAvisoDaLeitura,
} from '../../utils/empenhoDoPdf';

/**
 * AS NOTAS DE EMPENHO DA ASSINATURA (16/10/2026, fase 2 do plano de empenho, aprovada pelo Heitor: "lista na aba
 * Contrato e reajuste, perto do campo de hoje"). Ver utils/empenho. Ler é de todos; registrar, editar, excluir e anexar
 * são do admin (as rotas do backend #468 são ADMIN).
 */
type Props = { contractId?: number | null; itens: any[]; podeEditar: boolean };

const motivoDoServidor = (e: any) => String(e?.response?.data?.message || mensagemDoErroDeCarga(e));

/** A zona de PDF: clique ou arraste (padrão obrigatório da casa). Só PDF. */
function ZonaDePdf({ arquivo, onArquivo, desligada, rotulo }: { arquivo: File | null; onArquivo: (f: File | null) => void; desligada?: boolean; rotulo: string }) {
  const [sobre, setSobre] = useState(false);
  const entrada = useRef<HTMLInputElement>(null);
  const receber = (lista: FileList | null | undefined) => { if (!desligada && lista && lista[0]) onArquivo(lista[0]); };
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); if (!desligada) setSobre(true); }}
      onDragLeave={() => setSobre(false)}
      onDrop={(e) => { e.preventDefault(); setSobre(false); receber(e.dataTransfer.files); }}
      onClick={() => !desligada && entrada.current?.click()}
      role='button' tabIndex={0} data-zona-do-pdf
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); entrada.current?.click(); } }}
      className={`rounded-lg border border-dashed px-3 py-3 text-sm text-center cursor-pointer border-central-borda text-slate-400 hover:text-white ${sobre ? 'ring-2 ring-primary' : ''} ${desligada ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {arquivo ? <span className='text-white'>{arquivo.name}</span> : rotulo}
      <input ref={entrada} type='file' accept='application/pdf,.pdf' className='hidden' onChange={(e) => { receber(e.target.files); e.target.value = ''; }} />
    </div>
  );
}

function Formulario({ contractId, produtos, original, onFechar, onGravou }: {
  contractId: number; produtos: Array<{ productId: number; nome: string }>; original: NotaDeEmpenho | null;
  onFechar: () => void; onGravou: (mensagem: string) => void;
}) {
  const [f, setF] = useState<FormularioDoEmpenho>(original ? formularioDe(original) : formularioVazio());
  const [pdf, setPdf] = useState<File | null>(null);
  const [previa, setPrevia] = useState<PreviaDoRetroConsumo | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  // O modo do escopo é estado PRÓPRIO: desmarcar o último produto não pode virar "o contrato inteiro" sem a pessoa pedir
  // (lista vazia é o que o backend lê como contrato inteiro).
  const [soProdutos, setSoProdutos] = useState(!!original?.productIds?.length);
  const falta = oQueFalta(f) || (soProdutos && f.productIds.length === 0 ? 'escolha ao menos um produto, ou marque o contrato inteiro.' : null);
  const setCampo = (k: keyof FormularioDoEmpenho, v: any) => { setF({ ...f, [k]: v }); setPrevia(null); setErro(null); };
  const escopoTodos = !soProdutos;
  // A leitura do PDF (fase 3): o que voltou e o formulário como ficou ao preencher, para o selo de cada campo saber se a
  // pessoa mudou o valor depois. Nada aqui grava: o cadastro segue pelo "Ver o que entra" e "Registrar".
  const [leitura, setLeitura] = useState<{ l: LeituraDoEmpenho; lido: FormularioDoEmpenho } | null>(null);
  const [lendo, setLendo] = useState(false);
  const [falhaDaLeitura, setFalhaDaLeitura] = useState<string | null>(null);

  const lerDoPdf = async (arq: File | null) => {
    if (!arq) return;
    if (!ehPdf(arq)) { setFalhaDaLeitura(`"${arq.name}" não é PDF. Só leio o PDF da nota de empenho.`); return; }
    setLendo(true); setFalhaDaLeitura(null);
    try {
      const r = await contractEmpenhosService.lerPdf(contractId, arq);
      // Resposta que não é leitura é falha, nunca formulário vazio.
      if (!ehLeitura(r)) throw new Error('a resposta do servidor não é uma leitura');
      const lido = formularioDaLeitura(f, r);
      setF(lido); setLeitura({ l: r, lido }); setPrevia(null); setErro(null);
      setPdf(arq);
    } catch (e) {
      // O formulário fica como estava: a recusa é dita, com o nome do arquivo (a leitura anterior, se houve, segue na
      // tela e é dela que o formulário fala), e nada é apagado nem preenchido.
      setFalhaDaLeitura(`"${arq.name}": ${fraseDaFalhaDaLeitura(e)}`);
    } finally {
      setLendo(false);
    }
  };

  const selo = (k: CampoDoFormularioLido) => {
    if (!leitura) return null;
    const s = seloDoCampo(k, leitura.l, leitura.lido, f);
    const [tom, texto] = s.tipo === 'alta' ? ['success', 'lido do PDF'] : s.tipo === 'media' ? ['warning', 'conferir'] : s.tipo === 'ausente' ? ['neutral', 'não está no PDF'] : ['neutral', 'à mão'];
    return (
      <span data-selo-do-campo={s.tipo} title={s.origem ? `Lido de: "${s.origem}"` : undefined}
        className={`ml-1 px-1.5 py-px rounded-full text-[10px] align-middle ${TONE_BADGE[tom as 'success' | 'warning' | 'neutral']}`}>{texto}</span>
    );
  };
  const origem = (k: CampoDoFormularioLido) => {
    if (!leitura) return null;
    const s = seloDoCampo(k, leitura.l, leitura.lido, f);
    return s.tipo === 'media' && s.origem
      ? <p className={`mt-1 text-[11px] ${TONE_TEXT.warning}`} data-origem-do-campo={k}>Lido de: "{s.origem}"</p>
      : null;
  };

  const escolherPdf = (arq: File | null) => {
    if (arq && !ehPdf(arq)) { setErro(`"${arq.name}" não é PDF. O empenho aceita só o PDF da nota.`); return; }
    setErro(null); setPdf(arq);
  };

  const verPrevia = async () => {
    setEnviando(true); setErro(null);
    try { setPrevia(await contractEmpenhosService.previa(contractId, corpoDoEmpenho(f))); }
    catch (e) { setErro(motivoDoServidor(e)); }
    finally { setEnviando(false); }
  };

  const gravar = async () => {
    setEnviando(true); setErro(null);
    try {
      let id: number;
      let mensagem: string;
      if (original) {
        const r = await contractEmpenhosService.atualizar(original.id, corpoDoEmpenho(f));
        id = original.id;
        const avisos: string[] = Array.isArray(r?.avisos) ? r.avisos : [];
        mensagem = `Empenho ${f.number.trim()} salvo.${avisos.length ? ' ' + avisos.join(' ') : ''}`;
      } else {
        const r = await contractEmpenhosService.criar(contractId, corpoDoEmpenho(f));
        id = r?.empenho?.id;
        const entraram = r?.retroConsumo?.entram?.length || 0;
        mensagem = `Empenho ${f.number.trim()} registrado${entraram ? `; ${entraram} fatura(s) já aprovada(s) entraram nele` : ''}.`;
      }
      if (pdf && id) {
        try { await contractEmpenhosService.anexar(id, pdf); }
        catch (e) { mensagem += ` O PDF NÃO subiu: ${motivoDoServidor(e)}. Anexe pelo cartão do empenho.`; }
      }
      onGravou(mensagem);
    } catch (e) {
      setErro(motivoDoServidor(e));
    } finally {
      setEnviando(false);
    }
  };

  // Registrar novo passa pela prévia (as faturas já aprovadas da faixa entram); editar não refaz esse consumo.
  const primaria = original
    ? { rotulo: enviando ? 'Salvando…' : 'Salvar', acao: gravar }
    : previa ? { rotulo: enviando ? 'Registrando…' : 'Registrar', acao: gravar } : { rotulo: enviando ? 'Calculando…' : 'Ver o que entra', acao: verPrevia };

  return (
    <CascaDeModal
      largura='md'
      titulo={original ? `Editar empenho ${original.number}` : 'Registrar nota de empenho'}
      onClose={onFechar}
      corpoClassName='px-5 py-4 space-y-3'
      rodape={
        <>
          <span className='text-xs text-slate-400 mr-auto'>{falta ? `Falta: ${falta}` : ''}</span>
          <button type='button' onClick={onFechar} className={`px-4 py-2 rounded-lg text-sm ${acaoSecundaria}`}>Cancelar</button>
          <button type='button' onClick={primaria.acao} disabled={!!falta || enviando} data-acao-do-empenho
            className={`px-4 py-2 rounded-lg text-sm disabled:opacity-50 ${acaoPrincipal}`}>{primaria.rotulo}</button>
        </>
      }
    >
      {!original && (
        <section className='space-y-2' data-ler-do-pdf>
          <ZonaDePdf arquivo={null} onArquivo={lerDoPdf} desligada={lendo || enviando}
            rotulo={lendo ? 'Lendo o PDF…' : 'Ler do PDF: arraste a nota de empenho aqui ou clique. Preenche o formulário, não salva.'} />
          {falhaDaLeitura && (
            <p role='alert' className={`text-sm ${TONE_TEXT.danger}`} data-recusa-da-leitura>
              {falhaDaLeitura} Nada foi preenchido: o formulário segue como estava.
            </p>
          )}
          {leitura && (
            <div className='rounded-lg border border-central-borda p-3 space-y-1.5' data-leitura-do-pdf>
              <p className='text-sm text-slate-200'>
                Lido por <b className='text-white' data-reconhecedor>{ROTULO_DO_RECONHECEDOR[leitura.l.reconhecedor] || leitura.l.reconhecedor}</b>. Nada foi salvo: confira cada campo e registre.
              </p>
              {leitura.l.avisos.map((a, i) => (
                <p key={`${a.codigo}-${i}`} className={`text-sm font-medium ${TONE_TEXT[tomDoAvisoDaLeitura(a.codigo)]}`} data-aviso-da-leitura={a.codigo}>
                  ⚠ {ROTULO_DO_AVISO_DA_LEITURA[a.codigo] || a.codigo}: {a.mensagem}
                </p>
              ))}
            </div>
          )}
        </section>
      )}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
        <div><label htmlFor='emp-numero' className='block text-xs text-slate-400 mb-1'>Número *{selo('number')}</label>
          <input id='emp-numero' value={f.number} onChange={(e) => setCampo('number', e.target.value)} maxLength={60} placeholder='ex.: 29257/2026' className={`w-full px-3 py-2 rounded-lg ${campo}`} />{origem('number')}</div>
        <div><label htmlFor='emp-emissao' className='block text-xs text-slate-400 mb-1'>Emissão *{selo('issuedAt')}</label>
          <input id='emp-emissao' type='date' value={f.issuedAt} onChange={(e) => setCampo('issuedAt', e.target.value)} className={`w-full px-3 py-2 rounded-lg ${campo}`} /></div>
        <div><label htmlFor='emp-valor' className='block text-xs text-slate-400 mb-1'>Valor total (R$) *{selo('totalValue')}</label>
          <input id='emp-valor' inputMode='decimal' value={f.totalValue} onChange={(e) => setCampo('totalValue', e.target.value)} placeholder='29.300,00' className={`w-full px-3 py-2 rounded-lg tabular-nums ${campo}`} />{origem('totalValue')}</div>
        <div><label htmlFor='emp-inicio' className='block text-xs text-slate-400 mb-1'>Competência inicial *{selo('competenciaInicio')}</label>
          <input id='emp-inicio' type='month' value={f.competenciaInicio} onChange={(e) => setCampo('competenciaInicio', e.target.value)} className={`w-full px-3 py-2 rounded-lg ${campo}`} />{origem('competenciaInicio')}</div>
        <div><label htmlFor='emp-fim' className='block text-xs text-slate-400 mb-1'>Competência final *{selo('competenciaFim')}</label>
          <input id='emp-fim' type='month' value={f.competenciaFim} onChange={(e) => setCampo('competenciaFim', e.target.value)} className={`w-full px-3 py-2 rounded-lg ${campo}`} />{origem('competenciaFim')}</div>
      </div>
      <fieldset className='rounded-lg border border-central-borda p-3 min-w-0'>
        <legend className='px-1 text-xs text-slate-400'>O que o empenho cobre</legend>
        <label className='flex items-center gap-2 text-sm text-slate-200'>
          <input type='radio' name='emp-escopo' checked={escopoTodos} onChange={() => { setSoProdutos(false); setCampo('productIds', []); }} /> O contrato inteiro
        </label>
        <label className='flex items-center gap-2 text-sm text-slate-200 mt-1'>
          <input type='radio' name='emp-escopo' checked={!escopoTodos} disabled={produtos.length === 0}
            onChange={() => setSoProdutos(true)} /> Só estes produtos
        </label>
        {!escopoTodos && (
          <div className='mt-2 pl-6 space-y-1'>
            {produtos.map((p) => (
              <label key={p.productId} className='flex items-center gap-2 text-sm text-slate-300'>
                <input type='checkbox' checked={f.productIds.includes(p.productId)}
                  onChange={(e) => setCampo('productIds', e.target.checked ? [...f.productIds, p.productId] : f.productIds.filter((x) => x !== p.productId))} />
                {p.nome}
              </label>
            ))}
            <p className='text-xs text-slate-500'>Ex.: na FUNDETEC, um empenho para o STFC e outro para a locação.</p>
          </div>
        )}
      </fieldset>
      <div><label htmlFor='emp-obs' className='block text-xs text-slate-400 mb-1'>Observação</label>
        <input id='emp-obs' value={f.notes} onChange={(e) => setCampo('notes', e.target.value)} maxLength={300} className={`w-full px-3 py-2 rounded-lg ${campo}`} /></div>
      <ZonaDePdf arquivo={pdf} onArquivo={escolherPdf} desligada={enviando}
        rotulo={original?.fileName ? `PDF atual: ${original.fileName}. Arraste outro para trocar, ou clique` : 'PDF da nota de empenho: arraste aqui ou clique'} />
      {erro && <p role='alert' className={`text-sm ${TONE_TEXT.danger}`}>{erro}</p>}
      {previa && (
        <section className='rounded-lg border border-central-borda p-3 space-y-2' data-previa-do-empenho>
          <p className='text-sm text-white'>
            {previa.entram.length ? 'Entram neste empenho as faturas já aprovadas da faixa:' : 'Nenhuma fatura aprovada da faixa entra agora.'}
          </p>
          {previa.entram.length > 0 && (
            <ul className='text-sm text-slate-300 space-y-0.5'>
              {previa.entram.map((x) => (
                <li key={x.invoiceId} className='flex justify-between gap-3'><span>{fmtCompetencia(x.competencia)} · {x.code}</span><span className='tabular-nums'>{fmtCurrency(x.valor)}</span></li>
              ))}
            </ul>
          )}
          <p className='text-sm text-slate-300'>O empenho começa com saldo <b className={previa.saldoResultante.valor < 0 ? TONE_TEXT.danger : 'text-white'}>{fmtSaldo(previa.saldoResultante.valor)}</b> · {fraseDasCompetencias(previa.saldoResultante.competencias)}.</p>
          {previa.avisos.map((a, i) => (
            <p key={i} className={`text-sm ${TONE_TEXT.warning}`}>⚠ {ROTULO_DO_AVISO[a.codigo] || a.codigo}: {a.mensagem}</p>
          ))}
        </section>
      )}
    </CascaDeModal>
  );
}

export default function NotasDeEmpenho({ contractId, itens, podeEditar }: Props) {
  const confirmar = useConfirm();
  const avisar = useAviso();
  const { rodar, ...carga } = useEstadoDaCarga();
  const [lista, setLista] = useState<NotaDeEmpenho[]>([]);
  const [formulario, setFormulario] = useState<{ original: NotaDeEmpenho | null } | null>(null);
  const produtos = useMemo(() => produtosDaAssinatura(itens), [itens]);

  const carregar = useCallback(async () => {
    if (!contractId) return;
    await rodar(async () => {
      const r = await contractEmpenhosService.listar(contractId);
      setLista(Array.isArray(r) ? r : []);
    });
  }, [contractId, rodar]);
  useEffect(() => { carregar(); }, [carregar]);

  if (!contractId) {
    return <p className='text-sm text-slate-500' data-notas-de-empenho>Salve a assinatura para registrar notas de empenho.</p>;
  }

  const verPdf = async (e: NotaDeEmpenho) => {
    try { const { url } = await contractEmpenhosService.urlDoAnexo(e.id); window.open(url, '_blank', 'noopener'); }
    catch (x) { avisar(`Não consegui abrir o PDF do empenho ${e.number}: ${motivoDoServidor(x)}`, 'error'); }
  };
  const anexar = async (e: NotaDeEmpenho, arq: File | null) => {
    if (!arq) return;
    if (!ehPdf(arq)) { avisar(`"${arq.name}" não é PDF. O empenho aceita só o PDF da nota.`, 'error'); return; }
    try { await contractEmpenhosService.anexar(e.id, arq); avisar(`PDF anexado ao empenho ${e.number}.`, 'success'); carregar(); }
    catch (x) { avisar(`O PDF não subiu: ${motivoDoServidor(x)}`, 'error'); }
  };
  const excluir = async (e: NotaDeEmpenho) => {
    if (!(await confirmar({
      title: `Excluir a nota de empenho ${e.number}?`, danger: true, confirmLabel: 'Excluir',
      consequence: 'O registro e o PDF saem da assinatura. Empenho já consumido por fatura não pode ser excluído: o servidor recusa e diz quais faturas.',
    }))) return;
    try { await contractEmpenhosService.excluir(e.id); avisar(`Empenho ${e.number} excluído.`, 'success'); carregar(); }
    catch (x) { avisar(motivoDoServidor(x), 'error'); }
  };

  return (
    <section className='rounded-lg border border-central-borda p-4 space-y-3' data-notas-de-empenho>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <p className='text-sm font-medium text-slate-300'>Notas de empenho</p>
          <p className='text-xs text-slate-500'>Com saldo em valor e em competências. Cada fatura aprovada consome do empenho da competência. Assinatura sem empenho registrado fica fora do controle.</p>
          <p className='text-xs text-slate-500' data-campo-de-hoje-continua>O campo "Nº do Empenho" acima continua: entra no nome dos itens da nota quando nem o item nem um empenho registrado dizem outro número.</p>
        </div>
        {podeEditar && (
          <button type='button' onClick={() => setFormulario({ original: null })} className={`px-3 py-1.5 rounded-lg text-sm ${acaoSecundaria}`} data-registrar-empenho>+ Registrar empenho</button>
        )}
      </div>
      <EstadoDaLista carga={carga} temDados={lista.length > 0} oQue='as notas de empenho' onTentarDeNovo={carregar}
        vazio={<p className='text-sm text-slate-500'>Nenhuma nota de empenho registrada: esta assinatura está fora do controle de saldo.</p>}>
        <div className='space-y-2'>
          {lista.map((e) => {
            const s = e.saldo;
            const usado = s && s.competenciasTotal > 0 ? Math.min(100, Math.round((s.competenciasUsadas / s.competenciasTotal) * 100)) : 0;
            return (
              <div key={e.id} className='rounded-lg border border-central-borda bg-central-elevado/40 p-3 space-y-2' data-nota-de-empenho={e.number}>
                <div className='flex flex-wrap items-baseline justify-between gap-2'>
                  <p className='text-sm text-white'><b>{e.number}</b> <span className='text-slate-400'>· emitido em {fmtDate(e.issuedAt)}</span></p>
                  <p className='text-sm text-slate-300 tabular-nums'>{fmtCurrency(Number(e.totalValue))} · {faixaDoEmpenho(e)}</p>
                </div>
                <p className='text-xs text-slate-400'>Cobre: {escopoDoEmpenho(e.productIds, produtos)}</p>
                <div className='h-2 rounded-full bg-central-fundo overflow-hidden' aria-hidden='true'><div className='h-2 bg-primary' style={{ width: `${usado}%` }} /></div>
                <p className={`text-sm tabular-nums ${s && s.valor < 0 ? TONE_TEXT.danger : 'text-slate-200'}`} data-saldo-do-empenho>{fraseDoSaldo(s)}</p>
                {(e.consumos || []).length > 0 && (
                  <ul className='text-xs text-slate-400 space-y-0.5'>
                    {(e.consumos || []).map((c) => (
                      <li key={`${c.invoiceId}-${c.competencia}`} className='flex justify-between gap-3'><span>{fmtCompetencia(c.competencia)} · fatura #{c.invoiceId}</span><span className='tabular-nums'>{fmtCurrency(c.value)}</span></li>
                    ))}
                  </ul>
                )}
                {e.notes && <p className='text-xs text-slate-400'>{e.notes}</p>}
                <div className='flex flex-wrap items-center gap-2'>
                  {e.fileName
                    ? <button type='button' onClick={() => verPdf(e)} className={`px-3 py-1 rounded-lg text-xs ${acaoSecundaria}`}>Ver PDF</button>
                    : <span className={`px-2 py-0.5 rounded-full text-[11px] ${TONE_BADGE.warning}`}>sem PDF</span>}
                  {podeEditar && !e.fileName && (
                    <div className='min-w-[220px]'><ZonaDePdf arquivo={null} onArquivo={(f) => anexar(e, f)} rotulo='Anexar o PDF: arraste ou clique' /></div>
                  )}
                  {podeEditar && <button type='button' onClick={() => setFormulario({ original: e })} className={`px-3 py-1 rounded-lg text-xs ${acaoSecundaria}`}>Editar</button>}
                  {podeEditar && <button type='button' onClick={() => excluir(e)} className={`px-3 py-1 rounded-lg text-xs ${TONE_TEXT.danger}`}>Excluir</button>}
                </div>
              </div>
            );
          })}
        </div>
      </EstadoDaLista>
      {formulario && (
        <Formulario contractId={contractId} produtos={produtos} original={formulario.original}
          onFechar={() => setFormulario(null)}
          onGravou={(m) => { setFormulario(null); avisar(m, 'success'); carregar(); }} />
      )}
    </section>
  );
}
